/**
 * Sprint 103 — Los avisos de lo que pasa a domicilio, para quien no está mirando el tablero.
 *
 * Petición de la dueña, con su razón puesta: *"un globo que aparezca por unos segundos… esto por
 * si Claudia está en otra sección del sistema"*. Está sola y hace todo — cocina, cobra, contesta el
 * chat—, así que lo que ocurre en el tablero mientras ella está en Inventario o en Reportes no lo
 * ve nadie.
 *
 * ## Qué se avisa, y qué no
 *
 * Sólo **lo que empieza a esperar por alguien**: un pedido que entró solo por el enlace, uno que
 * quedó esperando cotización, un cliente que dice que ya transfirió, y uno que confirmó que
 * recibió. Cuatro cosas.
 *
 * No se avisa cada cambio de estado. Un aviso que aparece cada minuto se ignora en dos días, y
 * entonces no sirve el día que trae algo importante. Ésta es la diferencia entre una alerta y un
 * ruido de fondo.
 *
 * ## Módulo puro
 *
 * Recibe dos fotos del tablero —la anterior y la de ahora— y devuelve qué hay de nuevo. Sin red,
 * sin React y sin reloj propio: así se puede probar entero, que es lo que hace confiable un
 * mecanismo que sólo se dispara cuando nadie está mirando.
 */

export type TipoAviso =
  | 'llego_del_enlace'
  | 'falta_cotizar'
  | 'aviso_pago'
  | 'cliente_recibio';

/** Lo mínimo de cada pedido que hace falta para comparar dos fotos del tablero. */
export interface PedidoParaAviso {
  id: number;
  numeroPedido: number;
  motivo: string;
  clienteNombre?: string | null;
  /** ISO de cuándo el cliente dijo "ya transferí". */
  avisoPagoCliente?: string | null;
  /** ISO de cuándo el cliente confirmó que le llegó. */
  fechaRecibidoCliente?: string | null;
}

export interface Aviso {
  /** Estable por pedido + tipo: sirve para no repetir el mismo globo dos veces. */
  clave: string;
  tipo: TipoAviso;
  pedidoId: number;
  numeroPedido: number;
  titulo: string;
  detalle: string;
}

const CLIENTE = (p: PedidoParaAviso) => (p.clienteNombre ?? '').trim() || 'Un cliente';
const FOLIO = (p: PedidoParaAviso) => `#${String(p.numeroPedido).padStart(5, '0')}`;

/**
 * Qué pasó entre estas dos fotos del tablero.
 *
 * `antes = null` significa **primera carga**: no se avisa nada. Al abrir el sistema con nueve
 * pedidos en curso, nueve globos a la vez no informan de nada — sólo tapan la pantalla. La foto
 * inicial existe para tener contra qué comparar, no para anunciarse.
 */
export function avisosNuevos(
  antes: PedidoParaAviso[] | null,
  ahora: PedidoParaAviso[],
): Aviso[] {
  if (antes === null) return [];

  const previo = new Map(antes.map((p) => [p.id, p]));
  const avisos: Aviso[] = [];

  for (const p of ahora) {
    const anterior = previo.get(p.id);

    /**
     * Un pedido que entró solo por el enlace y que nadie ha mirado.
     *
     * Es el único que puede ser mentira —lo escribió un desconocido— y por eso es también el más
     * urgente de ver: hasta que alguien lo revise no hay nada cocinándose.
     */
    if (!anterior && p.motivo === 'llego_del_enlace') {
      avisos.push({
        clave: `${p.id}:llego_del_enlace`,
        tipo: 'llego_del_enlace',
        pedidoId: p.id,
        numeroPedido: p.numeroPedido,
        titulo: 'Llegó un pedido del enlace',
        detalle: `${FOLIO(p)} · ${CLIENTE(p)} lo armó solo. Revísalo antes de cocinar.`,
      });
      continue;
    }

    // Entró al tablero esperando que alguien le pregunte el envío al grupo de motos.
    if (!anterior && p.motivo === 'falta_cotizar_envio') {
      avisos.push({
        clave: `${p.id}:falta_cotizar`,
        tipo: 'falta_cotizar',
        pedidoId: p.id,
        numeroPedido: p.numeroPedido,
        titulo: 'Un pedido espera su envío',
        detalle: `${FOLIO(p)} · ${CLIENTE(p)}. Falta cotizarlo con las motos.`,
      });
    }

    /**
     * El cliente dice que ya transfirió.
     *
     * Se compara contra la foto anterior —de `null` a fecha— y no contra "tiene fecha", porque lo
     * segundo volvería a avisar en cada refresco hasta que el pedido saliera del tablero.
     */
    if (anterior && !anterior.avisoPagoCliente && p.avisoPagoCliente) {
      avisos.push({
        clave: `${p.id}:aviso_pago`,
        tipo: 'aviso_pago',
        pedidoId: p.id,
        numeroPedido: p.numeroPedido,
        titulo: 'Un cliente dice que ya transfirió',
        detalle: `${FOLIO(p)} · ${CLIENTE(p)}. Búscalo en tu cuenta y confírmalo.`,
      });
    }

    // Confirmó que le llegó su pedido.
    if (anterior && !anterior.fechaRecibidoCliente && p.fechaRecibidoCliente) {
      avisos.push({
        clave: `${p.id}:cliente_recibio`,
        tipo: 'cliente_recibio',
        pedidoId: p.id,
        numeroPedido: p.numeroPedido,
        titulo: 'Un cliente ya recibió su pedido',
        detalle: `${FOLIO(p)} · ${CLIENTE(p)} confirmó que le llegó.`,
      });
    }
  }

  return avisos;
}

/** Clave donde el negocio ajusta cuántos segundos dura cada globo. */
export const CLAVE_SEGUNDOS_AVISO = 'domicilio_segundos_aviso';

/** Lo que dura un aviso si nadie lo cambió. Suficiente para leerlo sin quedarse estorbando. */
export const SEGUNDOS_AVISO_DEFAULT = 8;

/**
 * Lee el ajuste guardado como texto.
 *
 * Tolerante con lo que haya en la base: vacío, no numérico o absurdo cae al default. Un `0` haría
 * que el aviso desapareciera antes de poder leerlo, y un número enorme lo dejaría pegado en la
 * pantalla tapando el trabajo.
 */
export function leerSegundosAviso(valor?: string | null): number {
  const texto = (valor ?? '').trim();
  /*
    El vacío se comprueba aparte porque `Number('')` es **0**, no `NaN`: sin esta línea, "no hay
    ajuste guardado" se confundía con "el negocio pidió cero segundos" y el aviso salía con la
    duración mínima en vez de la de por omisión. Lo cazó su propio test.
  */
  if (!texto) return SEGUNDOS_AVISO_DEFAULT;
  const n = Number(texto);
  if (!Number.isFinite(n)) return SEGUNDOS_AVISO_DEFAULT;
  if (n < 2) return 2;
  if (n > 60) return 60;
  return Math.round(n);
}

/**
 * Sprint 108 — Dónde aparece el aviso.
 *
 * Reporte del dueño del producto, y es el defecto más caro que puede tener un aviso: *"ni lo vi,
 * porque llegó por la parte inferior derecha y yo estaba mirando al centro y arriba, donde están
 * los botones grandes"*.
 *
 * Un aviso que aparece donde nadie mira **no existe**, y encima gasta la confianza en el
 * mecanismo: la próxima vez que algo importante pase, nadie va a estar pendiente de esa esquina.
 *
 * El default cambia a **arriba y al centro** por lo mismo que él describe: la vista vive donde
 * están los botones grandes del Dashboard. Y se puede mover, porque dónde mira cada quien depende
 * de su pantalla y de dónde tenga puesto el WhatsApp.
 */
export const POSICIONES_AVISO = [
  'arriba-centro',
  'arriba-derecha',
  'arriba-izquierda',
  'abajo-derecha',
  'abajo-izquierda',
] as const;

export type PosicionAviso = (typeof POSICIONES_AVISO)[number];

/** Donde la vista ya está: encima de los botones grandes. */
export const POSICION_AVISO_DEFAULT: PosicionAviso = 'arriba-centro';

/** Clave donde el negocio guarda su preferencia. */
export const CLAVE_POSICION_AVISO = 'domicilio_posicion_aviso';

/** Cómo se lee cada una en pantalla, en palabras y no en coordenadas. */
export const ETIQUETA_POSICION: Record<PosicionAviso, string> = {
  'arriba-centro': 'Arriba, al centro',
  'arriba-derecha': 'Arriba, a la derecha',
  'arriba-izquierda': 'Arriba, a la izquierda',
  'abajo-derecha': 'Abajo, a la derecha',
  'abajo-izquierda': 'Abajo, a la izquierda',
};

/**
 * Lee la preferencia guardada.
 *
 * Tolerante con lo que haya en la base: un valor desconocido —de una versión futura, o escrito a
 * mano— cae al default en vez de dejar el aviso sin posición, que en CSS significa "en cualquier
 * parte".
 */
export function leerPosicionAviso(valor?: string | null): PosicionAviso {
  const v = (valor ?? '').trim();
  return (POSICIONES_AVISO as readonly string[]).includes(v)
    ? (v as PosicionAviso)
    : POSICION_AVISO_DEFAULT;
}

/** Las clases de posición. Se mantienen aquí para que la lógica se pruebe sin montar React. */
export function clasesDePosicion(p: PosicionAviso): string {
  switch (p) {
    case 'arriba-centro':
      return 'top-4 left-1/2 -translate-x-1/2';
    case 'arriba-derecha':
      return 'top-4 right-4';
    case 'arriba-izquierda':
      return 'top-4 left-4';
    case 'abajo-izquierda':
      return 'bottom-4 left-4';
    case 'abajo-derecha':
    default:
      return 'bottom-4 right-4';
  }
}

/**
 * Sprint 108 — Qué formas de empezar un pedido usa este negocio.
 *
 * Petición del dueño del producto al renombrar la tercera tarjeta: *"'Me lo dicta por teléfono'…
 * que espero que nunca se use, de hecho sería genial poder apagar los cards (configurable si ya no
 * tiene uso)"*.
 *
 * Detrás hay algo más grande que un interruptor: el sistema acumula caminos que **este** negocio no
 * usa, y cada uno que sobra es una decisión más en hora pico. Poder apagarlos es lo que lo mantiene
 * simple para el cliente de hoy sin recortarlo para el de mañana.
 *
 * **Apagar oculta la tarjeta, no cierra el camino.** Un pedido dictado por teléfono se sigue
 * pudiendo capturar desde el POS; lo que desaparece es el atajo de la portada. Un ajuste que borra
 * la única vía a un caso real se convierte en una llamada de soporte el día que ese caso aparece.
 */
export const TARJETAS_DOMICILIO = ['menu', 'chat', 'telefono'] as const;

export type TarjetaDomicilio = (typeof TARJETAS_DOMICILIO)[number];

/** Clave donde se guardan las apagadas, separadas por coma. */
export const CLAVE_TARJETAS_OCULTAS = 'domicilio_tarjetas_ocultas';

export const ETIQUETA_TARJETA: Record<TarjetaDomicilio, string> = {
  menu: 'Mándale el menú',
  chat: 'Te lo pidió por WhatsApp',
  telefono: 'Me lo dicta por teléfono',
};

/**
 * Qué tarjetas se ven, con la regla que impide dejar el tablero sin puertas.
 *
 * **Nunca se apagan las tres.** Si se apagaran todas, "A domicilio" se quedaría sin forma de
 * empezar un pedido y el sistema no diría por qué: una pantalla que sólo muestra columnas vacías
 * parece rota, no configurada. Ante un ajuste imposible, se ignora — no se obedece a ciegas.
 */
export function tarjetasVisibles(ocultasGuardadas?: string | null): TarjetaDomicilio[] {
  const ocultas = new Set(
    (ocultasGuardadas ?? '')
      .split(',')
      .map((x) => x.trim())
      .filter((x): x is TarjetaDomicilio =>
        (TARJETAS_DOMICILIO as readonly string[]).includes(x),
      ),
  );
  const visibles = TARJETAS_DOMICILIO.filter((t) => !ocultas.has(t));
  return visibles.length > 0 ? visibles : [...TARJETAS_DOMICILIO];
}
